import { LitElement, html, type PropertyValues } from 'lit';
import { toCSSValue } from '@loadsmart/miranda-tokens';

import type { SidebarProps } from './sidebar.types';
import { defaultSidebarProps } from './sidebar.constants';
import { sidebarStyles, getFlexDirectionValue } from './sidebar.styles';

export class Sidebar extends LitElement {
	static override styles = [sidebarStyles()];

	static override get properties() {
		return {
			gap: { type: String },
			sideWidth: { type: String, attribute: 'side-width' },
			minContentWidth: { type: String, attribute: 'min-content-width' },
			reversed: { type: Boolean },
		};
	}

	declare gap: SidebarProps['gap'];

	declare sideWidth: SidebarProps['sideWidth'];

	declare minContentWidth: SidebarProps['minContentWidth'];

	declare reversed: SidebarProps['reversed'];

	constructor() {
		super();

		this.gap = defaultSidebarProps.gap;
		this.sideWidth = defaultSidebarProps.sideWidth;
		this.minContentWidth = defaultSidebarProps.minContentWidth;
		this.reversed = defaultSidebarProps.reversed;
	}

	protected override update(
		changedProperties: PropertyValues<SidebarProps>,
	): void {
		if (changedProperties.has('gap')) {
			this.style.setProperty(
				'--m-sidebar-gap',
				(this.gap && toCSSValue(this.gap)) ??
					toCSSValue(defaultSidebarProps.gap),
			);
		}
		if (changedProperties.has('sideWidth')) {
			this.style.setProperty(
				'--m-sidebar-side-width',
				this.sideWidth || defaultSidebarProps.sideWidth,
			);
		}
		if (changedProperties.has('minContentWidth')) {
			this.style.setProperty(
				'--m-sidebar-min-content-width',
				this.minContentWidth || defaultSidebarProps.minContentWidth,
			);
		}
		if (changedProperties.has('reversed')) {
			this.style.setProperty(
				'--m-sidebar-flex-direction',
				getFlexDirectionValue(this.reversed),
			);
		}

		super.update(changedProperties);
	}

	override render() {
		return html`
			<slot name="sidebar"></slot>
			<slot name="content"></slot>
		`;
	}
}

window.customElements.define('m-sidebar', Sidebar);

declare global {
	interface HTMLElementTagNameMap {
		'm-sidebar': Sidebar;
	}
}
